import {makeObservable, observable, action, computed} from 'mobx';

/**
 * 操作类型
 * 替代原来散落的各种 xxxLoading 字段
 */
export type OperationType =
    | 'pause' // 暂停任务
    | 'resume' // 恢复任务
    | 'terminate' // 终止任务
    | 'cancel' // 取消任务
    | 'nextTask' // 进入下一个阶段
    | 'rebuild' // 重新构造
    | 'fetchProgress' // 获取进度
    | 'webIde' // WebIDE 加载
    | 'evolveStageRunSolutions'; // 加载续跑解列表

/**
 * 操作加载状态管理
 *
 * 职责:
 * - 管理各种操作的加载状态
 * - 提供统一的加载状态访问接口
 * - 替代原 TaskRunningStore 中的 loading, pauseTaskLoading, terminateTaskLoading 等
 *
 * 设计原则:
 * - 单一职责:只管理加载状态,不管理业务逻辑
 * - 用 Set 替代多个布尔字段,消除重复
 * - 提供辅助方法 withLoading 自动管理加载状态
 */
export class OperationLoadingStore {
    /**
     * 正在进行的操作集合
     * 替代原来的:
     * - loading
     * - pauseTaskLoading
     * - continueTaskLoading
     * - terminateTaskLoading
     * - nextTaskLoading
     * - rebuildTaskLoading
     * - webIdeLoading
     * - cancelLoading
     */
    loadingOps = new Set<OperationType>();

    constructor() {
        makeObservable(this, {
            loadingOps: observable,
            isPausing: computed,
            isResuming: computed,
            isTerminating: computed,
            isCancelling: computed,
            isLoadingNextTask: computed,
            isRebuilding: computed,
            isFetchingProgress: computed,
            isLoadingWebIde: computed,
            isLoadingEvolveStageRunSolutions: computed,
            hasAnyLoading: computed,
            loadingCount: computed,
            startOp: action,
            endOp: action,
            reset: action,
            startOps: action,
            endOps: action
        });
    }

    /**
     * 是否正在暂停
     */
    get isPausing(): boolean {
        return this.loadingOps.has('pause');
    }

    /**
     * 是否正在恢复
     */
    get isResuming(): boolean {
        return this.loadingOps.has('resume');
    }

    /**
     * 是否正在终止
     */
    get isTerminating(): boolean {
        return this.loadingOps.has('terminate');
    }

    /**
     * 是否正在取消
     */
    get isCancelling(): boolean {
        return this.loadingOps.has('cancel');
    }

    /**
     * 是否正在进入下一个阶段
     */
    get isLoadingNextTask(): boolean {
        return this.loadingOps.has('nextTask');
    }

    /**
     * 是否正在重新构造
     */
    get isRebuilding(): boolean {
        return this.loadingOps.has('rebuild');
    }

    /**
     * 是否正在获取进度
     */
    get isFetchingProgress(): boolean {
        return this.loadingOps.has('fetchProgress');
    }

    /**
     * 是否正在加载 WebIDE
     */
    get isLoadingWebIde(): boolean {
        return this.loadingOps.has('webIde');
    }

    /**
     * 是否正在加载续跑解列表
     */
    get isLoadingEvolveStageRunSolutions(): boolean {
        return this.loadingOps.has('evolveStageRunSolutions');
    }

    /**
     * 是否有任何操作正在进行
     */
    get hasAnyLoading(): boolean {
        return this.loadingOps.size > 0;
    }

    /**
     * 正在进行的操作数量
     */
    get loadingCount(): number {
        return this.loadingOps.size;
    }

    /**
     * 开始一个操作
     */
    startOp(op: OperationType) {
        this.loadingOps.add(op);
    }

    /**
     * 结束一个操作
     */
    endOp(op: OperationType) {
        this.loadingOps.delete(op);
    }

    /**
     * 重置所有加载状态
     */
    reset() {
        this.loadingOps.clear();
    }

    /**
     * 检查指定操作是否正在进行
     */
    isLoading(op: OperationType): boolean {
        return this.loadingOps.has(op);
    }

    /**
     * 辅助方法:自动管理加载状态
     *
     * 用法:
     * await loadingStore.withLoading('pause', async () => {
     *     await api.pauseTask(jobId);
     * });
     *
     * 优势:
     * - 自动在函数执行前设置 loading
     * - 自动在函数完成后清除 loading
     * - 即使抛出异常也会清除 loading
     */
    async withLoading<T>(op: OperationType, fn: () => Promise<T>): Promise<T> {
        this.startOp(op);
        try {
            return await fn();
        } finally {
            this.endOp(op);
        }
    }

    /**
     * 批量开始多个操作
     */
    startOps(...ops: OperationType[]) {
        ops.forEach(op => this.loadingOps.add(op));
    }

    /**
     * 批量结束多个操作
     */
    endOps(...ops: OperationType[]) {
        ops.forEach(op => this.loadingOps.delete(op));
    }
}
